import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Pencil,
  Trash2,
  Layers,
  Droplets,
  Flame,
  Gauge,
  Thermometer,
  Zap,
  Wind,
  Loader2,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
  Hash,

} from "lucide-react";

const ICON_OPTIONS = [
  { value: "droplets", label: "Water", icon: Droplets },
  { value: "flame", label: "Gas/Fire", icon: Flame },
  { value: "gauge", label: "Pressure", icon: Gauge },
  { value: "thermometer", label: "Temperature", icon: Thermometer },
  { value: "zap", label: "Electrical", icon: Zap },
  { value: "wind", label: "Air/Wind", icon: Wind },
];

const getIconComponent = (iconName) => {
  const iconOption = ICON_OPTIONS.find(i => i.value === iconName);
  return iconOption ? iconOption.icon : Layers;
};

const OBSERVATION_TYPE_OPTIONS = [
  { value: "symbol", label: "Pass/Fail" },
  { value: "text", label: "Text" },
];

const SOURCE_OPTIONS = [
  { value: "step_result", label: "AI Extracted", desc: "Auto-filled from AI analysis of a mapped step" },
  { value: "session_field", label: "Test Input", desc: "Filled by engineer before starting the test" },
  { value: "static", label: "Fixed Value", desc: "Always shows the same value in every report" },
  { value: "manual", label: "Manual Entry", desc: "QC engineer fills in at report time" },
];

const DEFAULT_COLUMN_LABELS = {
  particulars: "PARTICULARS",
  observation: "OBSERVATION",
  remarks: "REMARKS",
  required_value: "REQUIRED VALUE",
};

const normalizeTemplateRow = (row, sectionKey, idx) => ({
  id: row.id || `${sectionKey}_r${String(idx + 1).padStart(2, "0")}`,
  label: row.label || row.particulars || "",
  observationType: row.observationType || "symbol",
  source: row.source || "step_result",
  required: Boolean(row.required),
  separator: row.separator !== undefined ? row.separator : ", ",
  session_field: row.session_field || "",
  static_value: row.static_value || "",
  columns: row.columns || null,
  grid_config: row.grid_config || null,
  options: row.options || null,
});

const DEFAULT_GENERAL_FIELDS = [
  { key: "qc_engineer",         label: "Quality Control Engineer", required: false },
  { key: "date_of_inspection",  label: "Date of Inspection",       required: false },
  { key: "customer_name",       label: "Customer Name",            required: true  },
  { key: "customer_po_wo",      label: "Customer PO / WO No & Date", required: true },
  { key: "asl_so_no",           label: "ASL SO No. & Date",        required: true  },
  { key: "enclosure_serial_no", label: "Enclosure Serial No.",     required: false },
];

// Keys auto-filled from session — shown as read-only hint in UI
const AUTO_FILL_KEYS = {
  qc_engineer:         "engineer name",
  date_of_inspection:  "inspection date",
  enclosure_serial_no: "serial number",
};

const makeNewRow = (sectionKey, existingRows) => ({
  id: `${sectionKey}_r${String(existingRows.length + 1).padStart(2, "0")}`,
  label: "",
  observationType: "symbol",
  source: "step_result",
  required: false,
  separator: ", ",
  session_field: "",
  static_value: "",
  required_value: "",
  columns: null,
  grid_config: null,
  options: null,
});

const makeDefaultGrid = (rows, cols) => ({
  col_headers: Array.from({ length: cols }, (_, i) => `Column ${i + 1}`),
  show_index: false,
  configure_columns: false,
  rows: Array.from({ length: rows }, () => ({
    defaults: Array(cols).fill(""),
    cell_type: "text",
    cell_source: "manual",
  })),
});

const resizeGrid = (gc, newRows, newCols) => {
  const col_headers = Array.from({ length: newCols }, (_, i) => gc.col_headers?.[i] ?? `Column ${i + 1}`);
  const rows = Array.from({ length: newRows }, (_, i) => {
    const existing = gc.rows?.[i];
    return {
      defaults: Array.from({ length: newCols }, (_, j) => existing?.defaults?.[j] ?? ""),
      cell_type: existing?.cell_type || "text",
      cell_source: existing?.cell_source || "manual",
    };
  });
  return { ...gc, col_headers, rows };
};

const normalizeTemplate = (template) => {
  if (!template) return null;
  return {
    ...template,
    general_detail_fields: template.general_detail_fields ?? DEFAULT_GENERAL_FIELDS,
    sections: (template.sections || []).map((section) => ({
      ...section,
      rows: (section.rows || []).map((row, idx) => normalizeTemplateRow(row, section.key, idx)),
    })),
  };
};

export default function ProductTypesPage() {
  const { token } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const DEFAULT_HEADER_CONFIG = {
    org_name: "",
    org_address: [""],
    org_contact: "",
    org_logo_b64: "",
    org_website: "",
  };

  const [formData, setFormData] = useState({
    name: "",

    description: "",
    icon: "droplets",
    has_power_config: false,
    require_serial_number: true,
    default_power_options: ["24V DC", "230V AC"],
    default_test_input_fields: [],
    default_phases: ["phase_1", "phase_2", "phase_3"],
    has_qc_report: false,
    report_template: null,
    report_header_config: null,
  });
  const [showReportTemplate, setShowReportTemplate] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [newField, setNewField] = useState({ label: "", type: "text", required: true, placeholder: "", options: [], min: "", max: "" });
  const [newFieldOption, setNewFieldOption] = useState("");
  const [editingFieldIdx, setEditingFieldIdx] = useState(null);
  const [newPower, setNewPower] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/product-types`, { headers });
      setProductTypes(response.data);
    } catch (error) {
      toast.error("Failed to load product types");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchProductTypes();
  }, [fetchProductTypes]);

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,

        description: type.description,
        icon: type.icon,
        has_power_config: !!(type.default_power_options && type.default_power_options.length > 0),
        has_qc_report: !!type.report_template,
        require_serial_number: type.require_serial_number !== false,
        default_power_options: type.default_power_options || ["24V DC", "230V AC"],
        default_test_input_fields: type.default_test_input_fields || [],
        default_phases: type.default_phases,
        report_template: normalizeTemplate(type.report_template),
        report_header_config: type.report_header_config || null,
      });
      setNewField({ label: "", type: "text", required: true, placeholder: "", options: [] });
      setNewFieldOption("");
      setEditingFieldIdx(null);
      setShowReportTemplate(!!type.report_template);
      setShowBranding(false);
    } else {
      setEditingType(null);
      setFormData({
        name: "",
    
        description: "",
        icon: "droplets",
        has_power_config: false,
        has_qc_report: false,
        require_serial_number: true,
        default_power_options: ["24V DC", "230V AC"],
        default_test_input_fields: [],
        default_phases: ["phase_1", "phase_2", "phase_3"],
        report_template: null,
        report_header_config: null,
      });
      setNewField({ label: "", type: "text", required: true, placeholder: "", options: [] });
      setNewFieldOption("");
      setEditingFieldIdx(null);
      setShowReportTemplate(false);
      setShowBranding(false);
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }


    if (formData.report_template) {
      for (const section of formData.report_template.sections || []) {
        for (const row of section.rows || []) {
          if (!row.label?.trim() && row.observationType !== "grid") {
            toast.error(`Section "${section.title}" has a row with an empty label`);
            return;
          }
          if (row.source === "static" && !row.static_value?.trim()) {
            toast.error(`Row "${row.label}" in "${section.title}" needs a static value`);
            return;
          }
        }
      }
    }

    setSubmitting(true);
    try {
      const { has_power_config, has_qc_report, ...payload } = formData;
      if (!has_power_config) payload.default_power_options = null;
      // require_serial_number is already in payload as a boolean — no extra handling needed
      if (!has_qc_report) {
        payload.report_template = null;
        payload.report_header_config = null;
      }
      // Clean up report_header_config if no custom branding was actually set
      if (payload.report_header_config && !payload.report_header_config.org_name && !payload.report_header_config.org_logo_b64) {
        payload.report_header_config = null;
      }
      if (editingType) {
        await axios.put(`${API}/product-types/${editingType.id}`, payload, { headers });
        toast.success("Product type updated");
      } else {
        await axios.post(`${API}/product-types`, payload, { headers });
        toast.success("Product type created");
      }
      setShowDialog(false);
      fetchProductTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? Products using this type will be affected.")) return;
    try {
      await axios.delete(`${API}/product-types/${id}`, { headers });
      toast.success("Product type deleted");
      fetchProductTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const addPowerOption = () => {
    if (newPower.trim() && !formData.default_power_options.includes(newPower.trim())) {
      setFormData({ ...formData, default_power_options: [...formData.default_power_options, newPower.trim()] });
      setNewPower("");
    }
  };

  const removePowerOption = (power) => {
    if (formData.default_power_options.length > 1) {
      setFormData({ ...formData, default_power_options: formData.default_power_options.filter(p => p !== power) });
    }
  };


  const updateRow = (sIdx, rIdx, changes) => {
    const updated = [...formData.report_template.sections];
    const rows = [...updated[sIdx].rows];
    rows[rIdx] = { ...rows[rIdx], ...changes };
    updated[sIdx] = { ...updated[sIdx], rows };
    setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
  };

  const removeRow = (sIdx, rIdx) => {
    const updated = [...formData.report_template.sections];
    const rows = updated[sIdx].rows.filter((_, i) => i !== rIdx);
    updated[sIdx] = { ...updated[sIdx], rows };
    setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Product Types</h2>
          <p className="text-muted-foreground">Define product categories and their default configurations</p>
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-product-type-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product Type
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[860px]">
            <DialogHeader>
              <DialogTitle>{editingType ? "Edit Product Type" : "Add New Product Type"}</DialogTitle>
              <DialogDescription>
                Define a product category with power, test input fields, and QC report template
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto pr-1">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Water Analyzer"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value,
                      })
                    }
                    data-testid="type-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe this product type..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  data-testid="type-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="grid grid-cols-6 gap-2">
                  {ICON_OPTIONS.map((option) => {
                    const IconComp = option.icon;
                    return (
                      <Button
                        key={option.value}
                        type="button"
                        variant={formData.icon === option.value ? "default" : "outline"}
                        size="icon"
                        className="h-10 w-full"
                        onClick={() => setFormData({ ...formData, icon: option.value })}
                        title={option.label}
                      >
                        <IconComp className="w-5 h-5" />
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Power Config */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Power Supply Config
                  </Label>
                  <p className="text-xs text-muted-foreground">Enable if products require power supply selection at test time</p>
                </div>
                <Switch
                  checked={formData.has_power_config}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_power_config: checked })}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-blue-500" />
                    Serial Number Field
                  </Label>
                  <p className="text-xs text-muted-foreground">Require tester to enter a serial number when starting a test</p>
                </div>
                <Switch
                  checked={formData.require_serial_number}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_serial_number: checked })}
                />
              </div>

              {formData.has_power_config && (
                <div className="space-y-2 pl-1">
                  <Label>Default Power Options</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.default_power_options.map((power) => (
                      <Badge
                        key={power}
                        variant="secondary"
                        className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => removePowerOption(power)}
                      >
                        {power} ×
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., 48V DC"
                      value={newPower}
                      onChange={(e) => setNewPower(e.target.value)}
                      className="flex-1"
                      onKeyDown={(e) => e.key === "Enter" && addPowerOption()}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={addPowerOption}>Add</Button>
                  </div>
                </div>
              )}

              {/* Session Fields */}
              <div className="space-y-3 border-t pt-4">
                <Label className="flex items-center gap-2 text-sm font-semibold">
                  <FileText className="w-4 h-4 text-purple-500" />
                  Test Input Fields
                  <span className="text-xs text-muted-foreground font-normal">
                    Additional fields the engineer fills before starting each test
                  </span>
                </Label>

                {formData.default_test_input_fields.length > 0 && (
                  <div className="space-y-2">
                    {formData.default_test_input_fields.map((field, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2 ${editingFieldIdx === idx ? "bg-primary/10 ring-1 ring-primary" : "bg-muted/50"}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{field.label}</p>
                          <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5 flex-wrap">
                            <span>{field.key} · {field.type}</span>
                            {field.type === "select" && field.options?.length ? <span>({field.options.length} options)</span> : null}
                            {field.type === "number" && (field.min !== undefined || field.max !== undefined) ? (
                              <span>· {field.min !== undefined ? field.min : "…"} – {field.max !== undefined ? field.max : "…"}</span>
                            ) : null}
                            {field.required ? <span>· required</span> : null}
                          </p>
                        </div>
                        <Button
                          type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                          onClick={() => {
                            setNewField({
                              label: field.label,
                              type: field.type,
                              required: !!field.required,
                              placeholder: field.placeholder || "",
                              options: field.options || [],
                              min: field.min !== undefined && field.min !== null ? String(field.min) : "",
                              max: field.max !== undefined && field.max !== null ? String(field.max) : "",
                            });
                            setNewFieldOption("");
                            setEditingFieldIdx(idx);
                          }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              default_test_input_fields: formData.default_test_input_fields.filter((_, i) => i !== idx),
                            });
                            if (editingFieldIdx === idx) {
                              setNewField({ label: "", type: "text", required: true, placeholder: "", options: [] });
                              setNewFieldOption("");
                              setEditingFieldIdx(null);
                            }
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Field Label</Label>
                    <Input
                      placeholder="e.g., Batch Number"
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v, options: [] })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="select">Select (predefined options)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newField.type !== "select" && (
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Placeholder (optional hint for QC engineer)</Label>
                      <Input
                        placeholder="e.g., Enter batch number from label"
                        value={newField.placeholder}
                        onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>
                  )}
                  {newField.type === "number" && (
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Min value (optional)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 0"
                          value={newField.min}
                          onChange={(e) => setNewField({ ...newField, min: e.target.value })}
                          className="h-8 text-sm font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Max value (optional)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 1000"
                          value={newField.max}
                          onChange={(e) => setNewField({ ...newField, max: e.target.value })}
                          className="h-8 text-sm font-mono"
                        />
                      </div>
                    </div>
                  )}
                  {newField.type === "select" && (
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Options</Label>
                      {newField.options.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                          {newField.options.map((opt) => (
                            <Badge
                              key={opt}
                              variant="secondary"
                              className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground text-xs"
                              onClick={() => setNewField({ ...newField, options: newField.options.filter(o => o !== opt) })}
                            >
                              {opt} ×
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., Option A"
                          value={newFieldOption}
                          onChange={(e) => setNewFieldOption(e.target.value)}
                          className="h-8 text-sm flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newFieldOption.trim() && !newField.options.includes(newFieldOption.trim())) {
                              setNewField({ ...newField, options: [...newField.options, newFieldOption.trim()] });
                              setNewFieldOption("");
                            }
                          }}
                        />
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => {
                            if (newFieldOption.trim() && !newField.options.includes(newFieldOption.trim())) {
                              setNewField({ ...newField, options: [...newField.options, newFieldOption.trim()] });
                              setNewFieldOption("");
                            }
                          }}
                        >Add</Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newField.required}
                      onCheckedChange={(v) => setNewField({ ...newField, required: v })}
                    />
                    <Label className="text-xs">Required field</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingFieldIdx !== null && (
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={() => {
                          setNewField({ label: "", type: "text", required: true, placeholder: "", options: [] });
                          setNewFieldOption("");
                          setEditingFieldIdx(null);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        if (!newField.label.trim()) return;
                        if (newField.type === "select" && newField.options.length < 2) {
                          toast.error("Select fields need at least 2 options");
                          return;
                        }
                        const otherFields = editingFieldIdx !== null
                          ? formData.default_test_input_fields.filter((_, i) => i !== editingFieldIdx)
                          : formData.default_test_input_fields;

                        // Build clean field object — strip min/max for non-number types
                        const buildField = (base, key) => {
                          const f = { ...base, key };
                          if (f.type === "number") {
                            f.min = f.min !== "" && f.min !== undefined ? Number(f.min) : undefined;
                            f.max = f.max !== "" && f.max !== undefined ? Number(f.max) : undefined;
                            if (f.min === undefined) delete f.min;
                            if (f.max === undefined) delete f.max;
                          } else {
                            delete f.min; delete f.max;
                          }
                          return f;
                        };

                        if (editingFieldIdx !== null) {
                          // Key is preserved on edit — it's the stable identifier other data
                          // (field_option_config, qc_session_mappings, step conditions) references,
                          // and shouldn't change just because the label text was edited.
                          const key = formData.default_test_input_fields[editingFieldIdx].key;
                          const updatedFields = formData.default_test_input_fields.map((f, i) =>
                            i === editingFieldIdx ? buildField(newField, key) : f
                          );
                          setFormData({ ...formData, default_test_input_fields: updatedFields });
                        } else {
                          const key = newField.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
                          if (otherFields.some(f => f.key === key)) {
                            toast.error("A field with this label already exists");
                            return;
                          }
                          setFormData({
                            ...formData,
                            default_test_input_fields: [...formData.default_test_input_fields, buildField(newField, key)],
                          });
                        }
                        setNewField({ label: "", type: "text", required: true, placeholder: "", options: [], min: "", max: "" });
                        setNewFieldOption("");
                        setEditingFieldIdx(null);
                      }}
                    >
                      {editingFieldIdx !== null ? (
                        <><Pencil className="w-3 h-3 mr-1" /> Update Field</>
                      ) : (
                        <><Plus className="w-3 h-3 mr-1" /> Add Field</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* QC Report Toggle */}
              <div className="flex items-center justify-between rounded-lg border p-3 border-t mt-2">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    Custom QC Report
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable if this product type requires a QC inspection report
                  </p>
                </div>
                <Switch
                  checked={formData.has_qc_report}
                  onCheckedChange={(checked) => {
                    const update = { ...formData, has_qc_report: checked };
                    if (checked && !formData.report_template) {
                      update.report_template = {
                        title: `${formData.name || "Product"} Inspection Report`,
                        general_detail_fields: DEFAULT_GENERAL_FIELDS,
                        sections: [{ key: "section_a", title: "Section A", rows: [makeNewRow("section_a", [])] }],
                      };
                      setShowReportTemplate(true);
                    }
                    if (!checked) {
                      setShowReportTemplate(false);
                    }
                    setFormData(update);
                  }}
                />
              </div>

              {/* Report Branding */}
              {formData.has_qc_report && (
              <div className="space-y-2 border-t pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Report Branding
                    <span className="text-xs font-normal text-muted-foreground">(org logo & address in PDF header)</span>
                  </Label>
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={() => setShowBranding((v) => !v)}
                  >
                    {showBranding ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    {showBranding ? "Collapse" : "Configure"}
                  </Button>
                </div>

                {showBranding && (() => {
                  const hc = formData.report_header_config || DEFAULT_HEADER_CONFIG;
                  const setHc = (patch) => setFormData((prev) => ({
                    ...prev,
                    report_header_config: { ...(prev.report_header_config || DEFAULT_HEADER_CONFIG), ...patch },
                  }));
                  const addrLines = hc.org_address?.length > 0 ? hc.org_address : [""];
                  return (
                    <div className="space-y-3 bg-muted/50 p-3 rounded-lg">
                      {/* Logo upload */}
                      <div className="space-y-1.5">
                        <Label className="text-xs">Organisation Logo</Label>
                        <div className="flex items-center gap-3">
                          {hc.org_logo_b64 ? (
                            <div className="relative">
                              <img src={hc.org_logo_b64} alt="Logo" className="h-10 object-contain border rounded bg-white p-1" />
                              <button
                                type="button"
                                onClick={() => setHc({ org_logo_b64: "" })}
                                className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                              >×</button>
                            </div>
                          ) : (
                            <div className="h-10 w-20 border rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">No logo</div>
                          )}
                          <label className="cursor-pointer">
                            <span className="text-xs text-primary underline">Upload image</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = (ev) => setHc({ org_logo_b64: ev.target.result });
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>
                          <span className="text-xs text-muted-foreground">PNG/JPG, will appear in PDF header left and as watermark</span>
                        </div>
                      </div>

                      {/* Org name + website */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Organisation Name</Label>
                          <Input
                            value={hc.org_name || ""}
                            onChange={(e) => setHc({ org_name: e.target.value })}
                            placeholder="e.g., Brix Engineering Pte Ltd"
                            className="h-7 text-xs"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Website (footer left)</Label>
                          <Input
                            value={hc.org_website || ""}
                            onChange={(e) => setHc({ org_website: e.target.value })}
                            placeholder="e.g., www.brixengg.com"
                            className="h-7 text-xs font-mono"
                          />
                        </div>
                      </div>

                      {/* Address lines */}
                      <div className="space-y-1">
                        <Label className="text-xs">Address Lines</Label>
                        {addrLines.map((line, i) => (
                          <div key={i} className="flex gap-1.5 items-center">
                            <Input
                              value={line}
                              onChange={(e) => {
                                const updated = [...addrLines];
                                updated[i] = e.target.value;
                                setHc({ org_address: updated });
                              }}
                              placeholder={`Address line ${i + 1}`}
                              className="h-7 text-xs flex-1"
                            />
                            {addrLines.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                                onClick={() => setHc({ org_address: addrLines.filter((_, j) => j !== i) })}>
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs"
                          onClick={() => setHc({ org_address: [...addrLines, ""] })}>
                          <Plus className="w-3 h-3 mr-1" /> Add line
                        </Button>
                      </div>

                      {/* Contact line */}
                      <div className="space-y-1">
                        <Label className="text-xs">Contact / Email Line</Label>
                        <Input
                          value={hc.org_contact || ""}
                          onChange={(e) => setHc({ org_contact: e.target.value })}
                          placeholder="e.g., sales@brixengg.com, www.brixengg.com"
                          className="h-7 text-xs"
                        />
                      </div>

                    </div>
                  );
                })()}
              </div>
              )}

              {/* QC Report Template Editor */}
              {formData.has_qc_report && (
              <div className="space-y-2 border-t pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-muted-foreground">Template</Label>
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={() => setShowReportTemplate((v) => !v)}
                  >
                    {showReportTemplate ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    {showReportTemplate ? "Collapse" : "Edit Template"}
                  </Button>
                </div>

                {showReportTemplate && formData.report_template && (
                  <div className="space-y-3 bg-muted/50 p-3 rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Doc No.</Label>
                        <Input
                          value={formData.report_template.doc_no || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, report_template: { ...formData.report_template, doc_no: e.target.value } })
                          }
                          placeholder="e.g., F/QC/8.0/41, Rev.01"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    {/* General Details Fields */}
                    {(() => {
                      const gdf = formData.report_template.general_detail_fields;
                      const hasSection = gdf === null || gdf === undefined ? true : gdf.length > 0;
                      const setGdf = (val) => setFormData({ ...formData, report_template: { ...formData.report_template, general_detail_fields: val } });
                      if (!hasSection) {
                        return (
                          <div className="border rounded p-2 bg-background">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground italic">General Details section removed — not shown in PDF</span>
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs"
                                onClick={() => setGdf(DEFAULT_GENERAL_FIELDS)}>
                                <Plus className="w-3 h-3 mr-1" /> Restore section
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      const fields = gdf || DEFAULT_GENERAL_FIELDS;
                      return (
                        <div className="border rounded p-2 bg-background space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold">General Details Fields</Label>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Filled by engineer when creating the report</span>
                              <Button
                                type="button" variant="ghost" size="sm" className="h-6 text-xs text-destructive hover:text-destructive"
                                onClick={() => setGdf([])}
                              >
                                <X className="w-3 h-3 mr-1" /> Remove section
                              </Button>
                            </div>
                          </div>
                          {fields.map((field, fIdx) => (
                            <div key={fIdx} className="flex items-center gap-2 bg-muted/30 rounded px-2 py-1.5">
                              <Input
                                value={field.label}
                                onChange={(e) => {
                                  const updated = [...fields];
                                  updated[fIdx] = { ...updated[fIdx], label: e.target.value };
                                  setGdf(updated);
                                }}
                                placeholder="Field label"
                                className="h-7 text-xs flex-1"
                              />
                              <Input
                                value={field.default_value || ""}
                                onChange={(e) => {
                                  const updated = [...fields];
                                  updated[fIdx] = { ...updated[fIdx], default_value: e.target.value };
                                  setGdf(updated);
                                }}
                                placeholder="Default value (optional)"
                                className="h-7 text-xs flex-1"
                              />
                              <Button
                                type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                                onClick={() => setGdf(fields.filter((_, i) => i !== fIdx))}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button" variant="ghost" size="sm" className="h-6 text-xs"
                            onClick={() => {
                              setGdf([...fields, { key: `field_${fields.length + 1}`, label: "", default_value: "" }]);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Field
                          </Button>
                        </div>
                      );
                    })()}

                    {/* Source legend */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground border rounded px-2 py-1.5 bg-background">
                      <span className="font-medium">Row sources:</span>
                      <span><span className="font-mono text-blue-600">AI Extracted</span> — auto-filled from AI analysis of a mapped step</span>
                      <span><span className="font-mono text-purple-600">Test Input</span> — filled by engineer before starting the test</span>
                      <span><span className="font-mono text-green-600">Fixed Value</span> — always shows the same value in every report</span>
                    </div>

                    {(formData.report_template.sections || []).map((section, sIdx) => (
                      <div key={sIdx} className="border rounded p-2 bg-background space-y-2">
                        {/* Section header */}
                        <div className="flex items-center gap-2">
                          <Input
                            value={section.title}
                            onChange={(e) => {
                              const updated = [...formData.report_template.sections];
                              updated[sIdx] = { ...updated[sIdx], title: e.target.value };
                              setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
                            }}
                            placeholder="Section title"
                            className="h-7 text-sm flex-1"
                          />
                          <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                            <Switch
                              checked={!!section.show_required_value_column}
                              onCheckedChange={(val) => {
                                const updated = [...formData.report_template.sections];
                                updated[sIdx] = { ...updated[sIdx], show_required_value_column: val };
                                setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
                              }}
                              className="h-4 w-7"
                            />
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Req. value col</span>
                          </label>
                          <Button
                            type="button" variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => {
                              const updated = formData.report_template.sections.filter((_, i) => i !== sIdx);
                              setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>

                        {/* Column headings — customize the fixed PARTICULARS/OBSERVATION/REMARKS labels for this section */}
                        <div className="flex items-center gap-2 flex-wrap pl-1">
                          <span className="text-xs text-muted-foreground shrink-0">Column headings:</span>
                          {["particulars", "observation", "remarks", ...(section.show_required_value_column ? ["required_value"] : [])].map((field) => (
                            <Input
                              key={field}
                              value={section.column_labels?.[field] ?? DEFAULT_COLUMN_LABELS[field]}
                              onChange={(e) => {
                                const updated = [...formData.report_template.sections];
                                updated[sIdx] = {
                                  ...updated[sIdx],
                                  column_labels: { ...DEFAULT_COLUMN_LABELS, ...updated[sIdx].column_labels, [field]: e.target.value },
                                };
                                setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
                              }}
                              placeholder={DEFAULT_COLUMN_LABELS[field]}
                              className="h-6 text-xs w-32"
                            />
                          ))}
                        </div>

                        {/* Rows */}
                        {(section.rows || []).map((row, rIdx) => (
                          <div key={rIdx} className="ml-2 border rounded p-2 space-y-1.5 bg-muted/30">
                            {/* Line 1: number, label, obs type, source, delete */}
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-muted-foreground w-4 shrink-0">{rIdx + 1}</span>
                              <Input
                                value={row.label || ""}
                                onChange={(e) => updateRow(sIdx, rIdx, { label: e.target.value })}
                                placeholder="Check item name"
                                className="h-7 text-xs flex-1"
                              />
                              <Select
                                value={row.observationType || "symbol"}
                                onValueChange={(val) => {
                                  const changes = { observationType: val };
                                  if (val === "grid") {
                                    changes.source = "manual";
                                    changes.columns = null;
                                    if (!row.grid_config) changes.grid_config = makeDefaultGrid(3, 3);
                                  } else {
                                    if (row.observationType === "grid") {
                                      changes.source = "step_result";
                                      changes.grid_config = null;
                                    }
                                    changes.columns = null;
                                  }
                                  updateRow(sIdx, rIdx, changes);
                                }}
                              >
                                <SelectTrigger className="h-7 w-[110px] text-xs shrink-0"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {OBSERVATION_TYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                  <SelectItem value="grid">Grid</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select
                                value={row.observationType === "grid" ? "manual" : (row.source || "step_result")}
                                onValueChange={(val) => updateRow(sIdx, rIdx, { source: val })}
                                disabled={row.observationType === "grid"}
                              >
                                <SelectTrigger className="h-7 w-[140px] text-xs shrink-0"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SOURCE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      <div>
                                        <div className="font-medium">{opt.label}</div>
                                        <div className="text-xs text-muted-foreground">{opt.desc}</div>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                                onClick={() => removeRow(sIdx, rIdx)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>

                            {/* Line 2: source-specific fields */}
                            <div className="ml-5 flex items-center gap-3 flex-wrap">
                              {row.observationType === "grid" ? (
                                <div className="w-full space-y-2">
                                  {/* Grid size controls */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Label className="text-xs text-muted-foreground">Rows</Label>
                                    <Input
                                      type="number" min="1" max="10"
                                      value={row.grid_config?.rows?.length ?? 3}
                                      onChange={(e) => {
                                        const n = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
                                        updateRow(sIdx, rIdx, { grid_config: resizeGrid(row.grid_config || {}, n, row.grid_config?.col_headers?.length ?? 3) });
                                      }}
                                      className="h-6 text-xs w-14"
                                    />
                                    <Label className="text-xs text-muted-foreground">× Cols</Label>
                                    <Input
                                      type="number" min="1" max="5"
                                      value={row.grid_config?.col_headers?.length ?? 3}
                                      onChange={(e) => {
                                        const n = Math.min(5, Math.max(1, parseInt(e.target.value) || 1));
                                        updateRow(sIdx, rIdx, { grid_config: resizeGrid(row.grid_config || {}, row.grid_config?.rows?.length ?? 3, n) });
                                      }}
                                      className="h-6 text-xs w-14"
                                    />
                                    <span className="text-xs text-muted-foreground">(max 10 × 5)</span>
                                    <label className="flex items-center gap-1 ml-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={!!row.grid_config?.show_index}
                                        onChange={(e) => updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, show_index: e.target.checked } })}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs text-muted-foreground">Index</span>
                                    </label>
                                    <label className="flex items-center gap-1 ml-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={!!row.grid_config?.configure_columns}
                                        onChange={(e) => updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, configure_columns: e.target.checked } })}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs text-muted-foreground">Configure last column</span>
                                    </label>
                                  </div>

                                  {/* Grid editor table */}
                                  {row.grid_config && (() => {
                                    const lastColIdx = (row.grid_config.col_headers || []).length - 1;
                                    return (
                                    <div className="overflow-x-auto rounded border bg-background">
                                      <table className="text-xs border-collapse w-full">
                                        <thead>
                                          <tr className="bg-muted/60">
                                            {row.grid_config.show_index && (
                                              <th className="border-r border-b px-2 py-1 w-8 text-center text-muted-foreground font-medium">#</th>
                                            )}
                                            {(row.grid_config.col_headers || []).map((hdr, cIdx) => (
                                              <th key={cIdx} className="border-r border-b px-1 py-1">
                                                <Input
                                                  value={hdr}
                                                  onChange={(e) => {
                                                    const col_headers = [...row.grid_config.col_headers];
                                                    col_headers[cIdx] = e.target.value;
                                                    updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, col_headers } });
                                                  }}
                                                  placeholder={`Col ${cIdx + 1}`}
                                                  className="h-6 text-xs border-0 bg-transparent p-0 font-medium min-w-[80px]"
                                                />
                                                {row.grid_config.configure_columns && cIdx === lastColIdx && (
                                                  <span className="block text-[10px] text-amber-600 font-normal mt-0.5">per-row configured</span>
                                                )}
                                              </th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(row.grid_config.rows || []).map((gridRow, rI) => (
                                            <tr key={rI} className={rI % 2 === 1 ? "bg-muted/20" : ""}>
                                              {row.grid_config.show_index && (
                                                <td className="border-r border-b px-2 py-1 text-center text-muted-foreground w-8">{rI + 1}</td>
                                              )}
                                              {(gridRow.defaults || []).map((defVal, cI) => {
                                                if (row.grid_config.configure_columns && cI === lastColIdx) {
                                                  const cellType = gridRow.cell_type || "text";
                                                  const cellSource = gridRow.cell_source || "manual";
                                                  const updateCell = (changes) => {
                                                    const rows = [...row.grid_config.rows];
                                                    rows[rI] = { ...rows[rI], ...changes };
                                                    updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, rows } });
                                                  };
                                                  return (
                                                    <td key={cI} className="border-r border-b px-1 py-1 space-y-1 min-w-[110px]">
                                                      <Select value={cellType} onValueChange={(val) => updateCell({ cell_type: val })}>
                                                        <SelectTrigger className="h-6 w-full text-xs"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                          {OBSERVATION_TYPE_OPTIONS.map((opt) => (
                                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                          ))}
                                                        </SelectContent>
                                                      </Select>
                                                      <Select value={cellSource} onValueChange={(val) => updateCell({ cell_source: val })}>
                                                        <SelectTrigger className="h-6 w-full text-xs"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                          {SOURCE_OPTIONS.map((opt) => (
                                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                          ))}
                                                        </SelectContent>
                                                      </Select>
                                                      {cellSource === "step_result" ? (
                                                        <span className="block text-xs text-muted-foreground italic px-1 truncate">Set via step mapping</span>
                                                      ) : cellSource === "session_field" ? (
                                                        <span className="block text-xs text-muted-foreground italic px-1 truncate">Set via product session fields</span>
                                                      ) : (
                                                        <Input
                                                          value={defVal || ""}
                                                          onChange={(e) => {
                                                            const rows = [...row.grid_config.rows];
                                                            const defaults = [...(rows[rI].defaults || [])];
                                                            defaults[cI] = e.target.value;
                                                            rows[rI] = { ...rows[rI], defaults };
                                                            updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, rows } });
                                                          }}
                                                          placeholder="default…"
                                                          className="h-6 text-xs border rounded p-1 min-w-[90px]"
                                                        />
                                                      )}
                                                    </td>
                                                  );
                                                }
                                                return (
                                                  <td key={cI} className="border-r border-b px-1 py-1">
                                                    <Input
                                                      value={defVal || ""}
                                                      onChange={(e) => {
                                                        const rows = [...row.grid_config.rows];
                                                        const defaults = [...(rows[rI].defaults || [])];
                                                        defaults[cI] = e.target.value;
                                                        rows[rI] = { ...rows[rI], defaults };
                                                        updateRow(sIdx, rIdx, { grid_config: { ...row.grid_config, rows } });
                                                      }}
                                                      placeholder="default…"
                                                      className="h-6 text-xs border-0 bg-transparent p-0 min-w-[60px]"
                                                    />
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    );
                                  })()}
                                  <p className="text-xs text-muted-foreground">Pre-fill default values in the grid. Empty cells → QC engineer fills at report time. "Configure last column" lets you set each row's Type/Source individually for the last column, just like a normal row.</p>
                                </div>
                              ) : (row.source || "step_result") === "step_result" ? (
                                <div className="flex items-center gap-1.5">
                                  <Switch
                                    checked={Boolean(row.required)}
                                    onCheckedChange={(v) => updateRow(sIdx, rIdx, { required: v })}
                                    className="scale-75 origin-left"
                                  />
                                  <Label className="text-xs text-muted-foreground">Required (must map a step)</Label>
                                </div>
                              ) : (row.source || "step_result") === "session_field" ? (
                                <span className="text-xs text-muted-foreground italic">
                                  Mapped to a test input field when adding a product
                                </span>
                              ) : (row.source || "step_result") === "static" ? (
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Fixed value</Label>
                                  <Input
                                    value={row.static_value || ""}
                                    onChange={(e) => updateRow(sIdx, rIdx, { static_value: e.target.value })}
                                    placeholder="Always shown as-is in report"
                                    className="h-6 text-xs w-44"
                                  />
                                </div>
                              ) : (row.source || "step_result") === "manual" ? (
                                <div className="space-y-1 w-full">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Options</Label>
                                    {(row.options || []).map((opt, oIdx) => (
                                      <Badge key={oIdx} variant="secondary" className="gap-1 text-xs font-normal">
                                        {opt}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = (row.options || []).filter((_, i) => i !== oIdx);
                                            updateRow(sIdx, rIdx, { options: next.length ? next : null });
                                          }}
                                          className="ml-0.5 hover:text-destructive"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </Badge>
                                    ))}
                                    <Input
                                      placeholder="Add option, press Enter"
                                      className="h-6 text-xs w-36"
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && e.target.value.trim()) {
                                          updateRow(sIdx, rIdx, { options: [...(row.options || []), e.target.value.trim()] });
                                          e.target.value = "";
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground">No options = free text. With options = dropdown for tester.</p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ))}

                        <Button
                          type="button" variant="ghost" size="sm" className="ml-2 h-6 text-xs"
                          onClick={() => {
                            const updated = [...formData.report_template.sections];
                            const rows = [...updated[sIdx].rows, makeNewRow(section.key, updated[sIdx].rows)];
                            updated[sIdx] = { ...updated[sIdx], rows };
                            setFormData({ ...formData, report_template: { ...formData.report_template, sections: updated } });
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Row
                        </Button>
                      </div>
                    ))}

                    <div className="flex gap-2">
                      <Button
                        type="button" variant="outline" size="sm" className="text-xs"
                        onClick={() => {
                          const sections = [...(formData.report_template.sections || [])];
                          const letter = String.fromCharCode(65 + sections.length);
                          const key = `section_${letter.toLowerCase()}`;
                          sections.push({ key, title: `Section ${letter}`, column_labels: { ...DEFAULT_COLUMN_LABELS }, rows: [makeNewRow(key, [])] });
                          setFormData({ ...formData, report_template: { ...formData.report_template, sections } });
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Section
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm" className="text-xs text-destructive"
                        onClick={() => {
                          setFormData({ ...formData, has_qc_report: false, report_template: null });
                          setShowReportTemplate(false);
                        }}
                      >
                        Remove Template
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-type-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingType ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Product Types Grid */}
      {productTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No product types yet</p>
              <p className="text-sm text-muted-foreground">Add product types to categorize your products</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {productTypes.map((type) => {
            const IconComp = getIconComponent(type.icon);
            return (
              <Card key={type.id} className="card-hover" data-testid={`type-card-${type.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <IconComp className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{type.name}</CardTitle>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => handleOpenDialog(type)}
                        data-testid={`edit-type-${type.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(type.id)}
                        data-testid={`delete-type-${type.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">{type.description}</p>

                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Power Options</p>
                      {type.default_power_options && type.default_power_options.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {type.default_power_options.map((p) => (
                            <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">QC Report</p>
                      {type.report_template ? (
                        <p className="text-xs text-muted-foreground">
                          {type.report_template.sections?.length || 0} section(s),{" "}
                          {type.report_template.sections?.reduce((n, s) => n + (s.rows?.length || 0), 0) || 0} row(s)
                        </p>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>

                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
